import {useMemo} from 'react'
import {useQuery} from '@tanstack/react-query'
import {useNavigate} from 'react-router-dom'
import {usePageTitle} from '@/hooks/use-page-title'
import {useI18n} from '@/i18n'
import {useServices} from '@/services/context'
import {UnifiedSearchBar} from '@/components/shared/UnifiedSearchBar'
import {TableEmptyState} from '@/components/shared/TableEmptyState'
import {Button} from '@/components/ui/button'
import {Badge, BadgeVariant} from '@/components/ui/badge'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import {CONCEPT_ICON} from '@/lib/concepts'
import {Pagination} from '@/components/ui/pagination'
import {routes} from '@/lib/routes'
import {useServerPagination, SortDir} from '@/hooks/use-server-pagination'
import {IdentityControllerIdentitiesEndpoint} from '@/types/endpoint-contracts'
import {IdentityLifecycleStateBadge} from '@/types/identity'
import {useAppConfig, labelOf} from '@/hooks/use-app-config'
import {PageLayout} from '@/components/shared/PageLayout'
import {ClickableTableRow} from '@/components/shared/ClickableTableRow'
import {TextLink} from '@/components/shared/TextLink'

export function EffectiveAccessByIdentity() {
    const services = useServices()
    const i18n = useI18n()
    usePageTitle(i18n.analytics.byIdentityPageTitle)
    const navigate = useNavigate()
    const {identityTypes, lifecycleStates} = useAppConfig()

    const {
        page, pageSize, setPage, setPageSize, pageSizeOptions,
        queryString, searchAndFilters, setSearchAndFilters,
    } = useServerPagination({
        endpoint: IdentityControllerIdentitiesEndpoint,
        defaultSortBy: IdentityControllerIdentitiesEndpoint.sort.CREATED_AT,
        defaultSortDir: SortDir.DESC,
    })

    const hasActiveFilters = useMemo(() => {
        return !!searchAndFilters.q
            || Object.keys(searchAndFilters.filters).length > 0
            || Object.keys(searchAndFilters.dynamicAttributes).length > 0
    }, [searchAndFilters])

    // Fetch identities
    const {data: identitiesData, isLoading, error} = useQuery({
        queryKey: ['identities', queryString],
        queryFn: () => services.identities.list(Object.fromEntries(new URLSearchParams(queryString))),
    })

    const identities = identitiesData?.content || []
    const totalItems = identitiesData?.totalElements || 0
    const totalPages = identitiesData?.totalPages || 0

    return (
        <PageLayout
            data-testid="effective-access-by-identity-page"
            variant="list"
            icon={CONCEPT_ICON.identity}
            loading={isLoading}
            error={error}
            title={i18n.analytics.accessByIdentities as string}
            count={totalItems}
            description={searchAndFilters.q ? i18n.analytics.matchingSearch as string : undefined}
            filters={
                <UnifiedSearchBar
                    contract={IdentityControllerIdentitiesEndpoint}
                    value={searchAndFilters}
                    onChange={setSearchAndFilters}
                    attributeEntityType="identity"
                    placeholder={i18n.analytics.searchIdentitiesPlaceholder as string}
                    testIdNamespace="ea-by-identity"
                />
            }
            pagination={totalItems > 0 ? (
                <Pagination
                    page={page}
                    pageSize={pageSize}
                    totalItems={totalItems}
                    totalPages={totalPages}
                    onPageChange={setPage}
                    onPageSizeChange={setPageSize}
                    pageSizeOptions={pageSizeOptions}
                />
            ) : undefined}
            emptyState={
                <TableEmptyState
                    colSpan={5}
                    hasFilters={hasActiveFilters}
                    matchMessage={i18n.analytics.noIdentitiesMatch}
                    emptyMessage={i18n.analytics.noIdentitiesFound}
                />
            }
        >
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead data-testid="col-identity">{i18n.analytics.identity}</TableHead>
                                <TableHead data-testid="col-party">{i18n.analytics.party}</TableHead>
                                <TableHead data-testid="col-type">{i18n.common.type}</TableHead>
                                <TableHead data-testid="col-status">{i18n.common.status}</TableHead>
                                <TableHead data-testid="col-actions">{i18n.common.actions}</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {identities.map((identity) => (
                                <ClickableTableRow key={identity.id} to={routes.effectiveAccess.identities.detail(identity.id)}>
                                    <TableCell className="font-medium">
                                        <TextLink
                                            to={routes.effectiveAccess.identities.detail(identity.id)}
                                            testId={`link-identity-${identity.id}`}
                                            stopPropagation
                                        >
                                            {identity.party.displayName || identity.identityType}
                                        </TextLink>
                                    </TableCell>
                                    <TableCell>
                                        {identity.party.displayName || '-'}
                                    </TableCell>
                                    <TableCell>
                                        <Badge variant="outline" >
                                            {labelOf(identityTypes, identity.identityType)}
                                        </Badge>
                                    </TableCell>
                                    <TableCell>
                                        {(() => {
                                            const lifecycleBadge = IdentityLifecycleStateBadge.of(identity.lifecycleState)
                                            const StateIcon = lifecycleBadge?.icon
                                            return (
                                                <Badge variant={lifecycleBadge?.variant ?? BadgeVariant.OUTLINE} className="gap-1">
                                                    {StateIcon && <StateIcon className="h-3 w-3" aria-hidden="true"/>}
                                                    {labelOf(lifecycleStates, identity.lifecycleState)}
                                                </Badge>
                                            )
                                        })()}
                                    </TableCell>
                                    <TableCell>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={(e) => {
                                                e.stopPropagation()
                                                navigate(routes.effectiveAccess.identities.detail(identity.id))
                                            }}
                                            data-testid={`btn-view-access-${identity.id}`}
                                        >
                                            {i18n.analytics.viewAccess}
                                        </Button>
                                    </TableCell>
                                </ClickableTableRow>
                            ))}
                            {identities.length === 0 && (
                                <TableEmptyState
                                    colSpan={5}
                                    hasFilters={hasActiveFilters}
                                    matchMessage={i18n.analytics.noIdentitiesMatch}
                                    emptyMessage={i18n.analytics.noIdentitiesFound}
                                />
                            )}
                        </TableBody>
                    </Table>

        </PageLayout>
    )
}
